/**
 * Pure derivations behind the CSV-import verdict line (SPEC-AUDITOR-CSV-IMPORT.md
 * section 7). Kept out of the dialog so the wording is unit-testable.
 *
 * Unchanged rows are a COUNT only (spec section 5), so a 100%-unchanged file
 * lists nothing: month falls back to the server's `summary.month` and the shop
 * count is simply omitted rather than guessed from the listed subset.
 */
import dayjs from 'dayjs';

import type { ImportRows, ImportSummary } from '../../api/index.ts';

const DOT = ' · ';

/** Empty detail set. The apply response carries rows on the wire too; the UI
 *  type (ImportApplyResponse) narrows to the summary, so the success line
 *  renders its verdict with no rows to draw on. */
export const NO_IMPORT_ROWS: ImportRows = { added: [], updated: [], skippedSync: [], flagged: [] };

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`;
}

function monthLabelFrom(yearMonth: string): string | null {
  const parsed = dayjs(`${yearMonth}-01`);
  return parsed.isValid() ? parsed.format('MMMM YYYY') : null;
}

/** Dates of CLEAN classified rows only -- a flagged row's date may be the
 *  very thing that failed validation. */
function classifiedDates(rows: ImportRows): string[] {
  return [...rows.added, ...rows.updated, ...rows.skippedSync].map((r) => r.date);
}

/** "August 2026", or null when neither the server nor the listed rows decide it. */
export function importMonthLabel(summary: ImportSummary, rows: ImportRows): string | null {
  if (summary.month) return monthLabelFrom(summary.month);
  const months = new Set(classifiedDates(rows).map((d) => d.slice(0, 7)));
  if (months.size !== 1) return null;
  return monthLabelFrom([...months][0]!);
}

/** "5 new · 55 unchanged · 0 updated · 0 skipped · 0 need attention" */
export function importCountsPhrase(summary: ImportSummary): string {
  return [
    `${summary.added} new`,
    `${summary.unchanged} unchanged`,
    `${summary.updated} updated`,
    `${summary.skippedSync} skipped`,
    `${summary.flagged} need attention`,
  ].join(DOT);
}

/** The one-line verdict; undecidable head segments drop out silently. */
export function importVerdict(summary: ImportSummary, rows: ImportRows = NO_IMPORT_ROWS): string {
  const head = [
    importMonthLabel(summary, rows),
    summary.shops === undefined ? null : plural(summary.shops, 'shop'),
    plural(summary.total, 'row'),
  ].filter((part): part is string => part !== null);
  return `${head.join(DOT)} — ${importCountsPhrase(summary)}`;
}

/** True when every counted row was already in v2 exactly as the file states it. */
export function isQuietNonEvent(summary: ImportSummary): boolean {
  return summary.total > 0 && summary.unchanged === summary.total;
}

/** Capitalises a server-authored string for display without rewording it. */
export function sentenceCase(text: string): string {
  const trimmed = text.trim();
  if (trimmed === '') return text;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

/** Server reason codes render as prose without the UI owning a vocabulary:
 *  "manual_conflict" -> "Manual conflict"; already-prose reasons pass through. */
export function humanizeReason(reason: string): string {
  return sentenceCase(reason.replaceAll('_', ' '));
}

/** "Alex Auditor", falling back to the email when no display name is set. */
export function importedByLabel(displayName: string | null, email: string | null): string {
  if (displayName !== null && displayName.trim() !== '') return displayName;
  if (email !== null && email.trim() !== '') return email;
  return 'Unknown user';
}
